import { keywordExtractor } from './keywordExtractor';
import { filterMapper } from '../utils/filterMapper';
import { SearchFilter, SubFilter } from '../types/filter';
import { ExtractedKeywords } from './keywordExtractor';
import { useCollectionCache } from './collectionCache.service';
import { useAuthStore } from '../store/auth.store';
import { safeMigration } from '../config/featureFlags';

interface MappedFilter {
  type: string;
  value: any;
}

interface ExtractedData extends ExtractedKeywords {
  filters: MappedFilter[];
  explanation: string;
}

class FilterService {
  // Define primary and secondary collections
  private primaryCollections = ['tags', 'genres', 'keys', 'bpm'];
  private secondaryCollections = ['composer', 'composers', 'sessionartist', 'sessionartists', 'client', 'clients', 'agency', 'agencies', 'producer', 'producers', 'creativedirector', 'creativedirectors', 'studio', 'studios'];

  // Add role-aware filter types
  private getFilterTypes(role: string) {
    const baseFilters = ['tags', 'genres', 'keys', 'bpm'];
    if (['ADMIN', 'OPERATOR'].includes(role)) {
      return [...baseFilters, 'date', 'license', 'composers', 'sessionArtists', 'clients', 'agencies', 'producers', 'creativeDirectors', 'studios'];
    }
    return baseFilters;
  }

  private isSecondaryCollection(collection: string): boolean {
    return this.secondaryCollections.some(sec => 
      collection === sec || collection.startsWith(sec + 's') || collection.startsWith(sec + '/')
    );
  }

  private isPrimaryCollection(collection: string): boolean {
    return this.primaryCollections.some(pri => 
      collection === pri || collection.startsWith(pri + 's') || collection.startsWith(pri + '/')
    );
  }

  async processQuery(query: string, firebase: any): Promise<ExtractedData> {
    // Check if collection cache is initialized
    const collectionCache = useCollectionCache.getState();
    const { role } = useAuthStore.getState();
    const allowedFilterTypes = this.getFilterTypes(role);

    if (collectionCache.isInitialized) {
    }
    
    const extractedData = await keywordExtractor.extractKeywords(query);

    // Validate and filter out non-existent tags/genres from Gemini's response
    const validatedExtractedData = this.validateGeminiFilters(extractedData, collectionCache);

    // First, try to find direct matches using migration-safe approach
    const directMatches = validatedExtractedData.keywords && Array.isArray(validatedExtractedData.keywords) 
      ? await safeMigration(
          'useFilterService',
          // New hook method
          async () => {
            // For now, fallback to original method as filterMapper needs migration too
            return await filterMapper.findDirectMatches(validatedExtractedData.keywords || [], firebase);
          },
          // Original method (fallback)
          async () => {
            return await filterMapper.findDirectMatches(validatedExtractedData.keywords || [], firebase);
          },
          'FilterService-directMatches'
        )
      : [];

    // Separate matches into primary and secondary
    const primaryMatches = directMatches.filter(match => {
      // Get all possible collection identifiers from the match
      const collections = [
        match.filter.multi.value,
        ...(match.filter.subFilters.filters || []).map(f => f.key),
        ...(match.filter.subFilters.filters || []).map(f => f.path)
      ].filter(Boolean); // Remove undefined/null values

      // Check if any collection is a primary collection and none are secondary
      return collections.some(c => this.isPrimaryCollection(c)) &&
             !collections.some(c => this.isSecondaryCollection(c));
    });

    const secondaryMatches = directMatches.filter(match => {
      // Get all possible collection identifiers from the match
      const collections = [
        match.filter.multi.value,
        ...(match.filter.subFilters.filters || []).map(f => f.key),
        ...(match.filter.subFilters.filters || []).map(f => f.path)
      ].filter(Boolean); // Remove undefined/null values

      return collections.some(c => this.isSecondaryCollection(c));
    });

    // Create filters-based filters
    const filtersFilters = this.createFiltersFromFilters({
      ...(validatedExtractedData.filters || {}),
    }, allowedFilterTypes, validatedExtractedData.logic);

    // Combine primary matches and filters filters
    let filters: SearchFilter;
    if (primaryMatches.length > 0) {
      // Combine primary matches with filters filters
      const allFilters = [filtersFilters, ...primaryMatches.map(match => match.filter)];
      filters = this.combineFilters(allFilters);
    } else {
      // Use only filters-based filters if no primary matches
      filters = filtersFilters;
    }

    // Store secondary matches for later use
    if (secondaryMatches.length > 0) {
      validatedExtractedData.secondaryMatches = secondaryMatches.map(match => {
        // Get the label and collection from either multi or subFilters
        let label = '';
        let collection = '';
        
        if (match.filter.multi.active && match.filter.multi.data?.length > 0) {
          label = match.filter.multi.data[0].label;
          collection = match.filter.multi.value;
        } else if (match.filter.subFilters.active && match.filter.subFilters.filters.length > 0) {
          label = match.filter.subFilters.filters[0].value.label;
          collection = match.filter.subFilters.filters[0].path;
        }

        return {
          label,
          collection,
          filter: match.filter
        };
      });
      
    }

    // Handle BPM filter from filters
    if (validatedExtractedData.filters?.bpm && allowedFilterTypes.includes('bpm')) {
      const bpmValue = validatedExtractedData.filters.bpm;
      if (bpmValue.includes('-')) {
        // Handle BPM range (including partial ranges like "140-" or "-80")
        const [minStr, maxStr] = bpmValue.split('-');
        const min = minStr && minStr.trim() !== '' ? Number(minStr) : null;
        const max = maxStr && maxStr.trim() !== '' ? Number(maxStr) : null;
        
        // Handle complete range (min-max)
        if (min !== null && max !== null && !isNaN(min) && !isNaN(max)) {
          filters.subFilters.filters.push({
            key: 'bpm',
            path: 'bpm',
            value: {
              label: `${min}-${max}`,
              value: `${min}-${max}`
            }
          });
        }
        // Handle minimum-only range (min-)
        else if (min !== null && !isNaN(min) && (maxStr === '' || maxStr === undefined)) {
          filters.subFilters.filters.push({
            key: 'bpm',
            path: 'bpm',
            value: {
              label: `${min}+`,
              value: `${min}-`
            }
          });
        }
        // Handle maximum-only range (-max)
        else if (max !== null && !isNaN(max) && (minStr === '' || minStr === undefined)) {
          filters.subFilters.filters.push({
            key: 'bpm',
            path: 'bpm',
            value: {
              label: `<${max}`,
              value: `-${max}`
            }
          });
        }
      } else {
        // Handle single BPM
        const bpm = Number(bpmValue);
        if (!isNaN(bpm)) {
          filters.subFilters.filters.push({
            key: 'bpm',
            path: 'bpm',
            value: {
              label: bpm.toString(),
              value: bpm.toString()
            }
          });
        }
      }
    }

    // Handle date keywords
    if (validatedExtractedData.filters && Array.isArray(validatedExtractedData.filters.date) && allowedFilterTypes.includes('date')) {
      const dateKeywords = validatedExtractedData.filters.date.filter((dateStr: string) =>
        dateStr.match(/^[0-9]{1,2}\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+[0-9]{4}$/i) ||
        dateStr.match(/^(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+[0-9]{1,2}(?:st|nd|rd|th)?,\s+[0-9]{4}$/i)
      );

      if (dateKeywords.length >= 2) {
        // Sort dates to ensure start date is before end date
        const sortedDates = dateKeywords.sort((a: string, b: string) => {
          const dateA = new Date(a);
          const dateB = new Date(b);
          return dateA.getTime() - dateB.getTime();
        });

        const startDate = new Date(sortedDates[0]);
        const endDate = new Date(sortedDates[sortedDates.length - 1]);
        endDate.setHours(23, 59, 59, 999);

        filters.date = {
          active: true,
          from: startDate.getTime(),
          to: endDate.getTime(),
          type: 'completed'
        };
        filters.activeFilterGroups.date = true;
      } else if (dateKeywords.length === 1) {
        // Single date - use as start date
        const date = new Date(dateKeywords[0]);
        filters.date = {
          active: true,
          from: date.getTime(),
          to: Date.now(),
          type: 'completed'
        };
        filters.activeFilterGroups.date = true;
      }
    }

    // Handle license filter
    if (validatedExtractedData.filters?.license && allowedFilterTypes.includes('license')) {
      filters.license = {
        active: true,
        value: validatedExtractedData.filters.license === 'licensed'
      };
      filters.activeFilterGroups.license = true;
    }

    // Handle studio filter
    if (validatedExtractedData.filters?.studio) {
      // Get the collection cache state
      const collectionCache = useCollectionCache.getState();
      
      // Check if cache is initialized and studio collection exists
      if (collectionCache.isInitialized && collectionCache.studio) {
        // Find all studio matches that include the keyword
        const studioMatches = collectionCache.studio.filter(item => {
          const label = item.displayName || item.label || item.name || '';
          return label.toLowerCase().includes(validatedExtractedData.filters.studio.toLowerCase());
        });
        
        // Add all matching studios to the filter
        studioMatches.forEach(studio => {
          filters.subFilters.filters.push({
            key: 'studio',
            path: 'studio',
            value: {
              label: studio.displayName || studio.label || studio.name,
              value: studio.displayName || studio.label || studio.name
            }
          });
        });
      } else {
        // Fallback to single studio match if cache not available
        filters.subFilters.filters.push({
          key: 'studio',
          path: 'studio',
          value: {
            label: validatedExtractedData.filters.studio,
            value: validatedExtractedData.filters.studio
          }
        });
      }
    }

    // --- Genre AND/OR logic ---
    let genreLogic: 'and' | 'or' = 'or';
    if (validatedExtractedData.logic?.genre === 'and' || validatedExtractedData.logic?.genre === 'or') {
      genreLogic = validatedExtractedData.logic.genre;
    }
    // --- Tag AND/OR logic ---
    let tagLogic: 'and' | 'or' = 'or';
    if (validatedExtractedData.logic?.tag === 'and' || validatedExtractedData.logic?.tag === 'or') {
      tagLogic = validatedExtractedData.logic.tag;
    }
    // --- Tag2 AND/OR logic ---
    let tag2Logic: 'and' | 'or' = 'or';
    if (validatedExtractedData.logic?.tag2 === 'and' || validatedExtractedData.logic?.tag2 === 'or') {
      tag2Logic = validatedExtractedData.logic.tag2;
    }
    // After all keyword/tag/genre matching logic, apply the logic to the filter:
    // For tags (multi)
    filters.multi.searchOperator = tagLogic.toUpperCase() as 'AND' | 'OR';
    // Store tag2 logic for later use
    if (validatedExtractedData.filters?.tags2) {
      (filters as any).tag2Logic = tag2Logic;
    }
    // For genres (subFilters)
    // If there are genre subFilters, store the logic in a property for later use (e.g., in the UI or when building the filter state)
    if (filters.subFilters.filters.some(f => f.key === 'genre')) {
      // Attach a custom property to subFilters for genre logic (if needed by downstream code)
      (filters.subFilters as any).genreLogic = genreLogic;
    }

    // Handle client filter
    if (validatedExtractedData.filters?.client) {
      // Convert to proper case (e.g., "Client Name" instead of "client name")
      const clientValue = validatedExtractedData.filters.client.split(' ').map((word: string) => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
      
      filters.subFilters.filters.push({
        key: 'client',
        path: 'client',
        value: {
          label: clientValue,
          value: clientValue
        }
      });
    }

    // Handle agency filter
    if (validatedExtractedData.filters?.agency) {
      filters.subFilters.filters.push({
        key: 'agency',
        path: 'agency',
        value: {
          label: validatedExtractedData.filters.agency,
          value: validatedExtractedData.filters.agency
        }
      });
    }

    // Handle producer filter
    if (validatedExtractedData.filters?.producer) {
      filters.subFilters.filters.push({
        key: 'producer',
        path: 'producer',
        value: {
          label: validatedExtractedData.filters.producer,
          value: validatedExtractedData.filters.producer
        }
      });
    }

    // Handle composer filter
    if (validatedExtractedData.filters?.composer) {
      filters.subFilters.filters.push({
        key: 'composer',
        path: 'composers',
        value: {
          label: validatedExtractedData.filters.composer,
          value: validatedExtractedData.filters.composer
        }
      });
    }

    // Handle key filter with consistent case handling
    if (validatedExtractedData.filters?.key) {
      // Convert to proper case (e.g., "A Minor" instead of "A minor")
      const keyValue = validatedExtractedData.filters.key.split(' ').map((word: string) => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
      
      filters.subFilters.filters.push({
        key: 'key',
        path: 'keys',
        value: {
          label: keyValue,
          value: keyValue
        }
      });
    }

    // Handle year filter
    if (validatedExtractedData.filters?.year) {
      filters.subFilters.filters.push({
        key: 'year',
        path: 'year',
        value: {
          label: validatedExtractedData.filters.year.toString(),
          value: validatedExtractedData.filters.year.toString()
        }
      });
    }

    const mappedFilters = this.convertToMappedFilters(filters);
    
    const explanation = this.generateExplanation(primaryMatches, validatedExtractedData);
    
    const result: ExtractedData = {
      ...validatedExtractedData,
      filters: mappedFilters,
      explanation
    };
    
    // Preserve original direct filters for UI processing
    if (validatedExtractedData.filters && (validatedExtractedData.filters.tags || validatedExtractedData.filters.tags2 || validatedExtractedData.filters.genres || validatedExtractedData.filters.key || validatedExtractedData.filters.agency || validatedExtractedData.filters.client || validatedExtractedData.filters.producer || validatedExtractedData.filters.studio || validatedExtractedData.filters.composers || validatedExtractedData.filters.creativedirectors || validatedExtractedData.filters.sessionartists || validatedExtractedData.filters.license)) {
      result.directFilters = {
        tags: validatedExtractedData.filters.tags,
        tags2: validatedExtractedData.filters.tags2,
        genres: validatedExtractedData.filters.genres,
        keys: validatedExtractedData.filters.key ? [validatedExtractedData.filters.key] : undefined,
        agencies: validatedExtractedData.filters.agency ? [validatedExtractedData.filters.agency] : undefined,
        clients: validatedExtractedData.filters.client ? [validatedExtractedData.filters.client] : undefined,
        producers: validatedExtractedData.filters.producer ? [validatedExtractedData.filters.producer] : undefined,
        studios: validatedExtractedData.filters.studio ? [validatedExtractedData.filters.studio] : undefined,
        composers: validatedExtractedData.filters.composers,
        creativeDirectors: validatedExtractedData.filters.creativedirectors,
        sessionArtists: validatedExtractedData.filters.sessionartists,
        license: validatedExtractedData.filters.license,
      };
      
      // Store the logic for UI processing
      if (validatedExtractedData.logic) {
        result.directFilters.logic = {
          tag: validatedExtractedData.logic.tag,
          tag2: validatedExtractedData.logic.tag2,
          genre: validatedExtractedData.logic.genre
        };
      }
    }

    return result;
  }

  private createFiltersFromFilters(filtersObj: any, allowedFilterTypes: string[], logic?: any): SearchFilter {
    // Normalize logic.tag and logic.genre to lowercase for case-insensitive handling
    if (logic && typeof logic === 'object') {
      if (typeof logic.tag === 'string') {
        logic.tag = logic.tag.toLowerCase();
      }
      if (typeof logic.genre === 'string') {
        logic.genre = logic.genre.toLowerCase();
      }
    }

    // Create a filter object with the same structure as direct matches
    const filter: SearchFilter = {
      multi: { active: false, data: [], value: '', searchOperator: 'OR' },
      date: { active: false, type: 'completed', from: 0, to: 0 },
      subFilters: { active: false, filters: [] },
      text: { active: false, value: '', field: 'trackName' },
      activeFilterGroups: {
        multi: false,
        date: false,
        single: false,
        text: false,
        license: false
      }
    };

    // Handle direct tags array (new direct filter support)
    if (Array.isArray(filtersObj.tags) && filtersObj.tags.length > 0 && allowedFilterTypes.includes('tags')) {
      const tagData = filtersObj.tags.map((tag: string) => ({
        label: tag,
        value: tag
      }));
      
      filter.multi = {
        active: true,
        data: tagData,
        value: 'tags',
        searchOperator: (logic?.tag === 'and') ? 'AND' : 'OR'
      };
      filter.activeFilterGroups.multi = true;
    }

    // Handle direct tags2 array (new dual selector support)
    if (Array.isArray(filtersObj.tags2) && filtersObj.tags2.length > 0 && allowedFilterTypes.includes('tags')) {
      const tag2Data = filtersObj.tags2.map((tag: string) => ({
        label: tag,
        value: tag
      }));
      
      // If we already have a multi filter (tags), add tags2 as subFilters with tag2 logic
      if (filter.multi.active) {
        filtersObj.tags2.forEach((tag: string) => {
          filter.subFilters.filters.push({
            key: 'tag2',
            path: 'tags',
            value: {
              label: tag,
              value: tag
            }
          });
        });
        filter.subFilters.active = true;
        filter.activeFilterGroups.single = true;
        
        // Store tag2 logic for later use
        (filter.subFilters as any).tag2Logic = (logic?.tag2 === 'and' || logic?.tag2 === 'or') ? logic.tag2 : 'or';
      } else {
        // No existing multi filter, use tags2 as multi filter
        filter.multi = {
          active: true,
          data: tag2Data,
          value: 'tags2',
          searchOperator: (logic?.tag2 === 'and') ? 'AND' : 'OR'
        };
        filter.activeFilterGroups.multi = true;
      }
    }

    // Handle direct genres array (new direct filter support)
    if (Array.isArray(filtersObj.genres) && filtersObj.genres.length > 0 && allowedFilterTypes.includes('genres')) {
      // If we already have a multi filter (tags), add genres as subFilters
      if (filter.multi.active) {
        filtersObj.genres.forEach((genre: string) => {
          filter.subFilters.filters.push({
            key: 'genre',
            path: 'genres',
            value: {
              label: genre,
              value: genre
            }
          });
        });
        filter.subFilters.active = true;
        filter.activeFilterGroups.single = true;
        
        // Store genre logic for later use
        if (logic?.genre) {
          (filter.subFilters as any).genreLogic = logic.genre;
        }
      } else {
        // No existing multi filter, use genres as multi filter
        const genreData = filtersObj.genres.map((genre: string) => ({
          label: genre,
          value: genre
        }));
        
        filter.multi = {
          active: true,
          data: genreData,
          value: 'genres',
          searchOperator: (logic?.genre === 'and') ? 'AND' : 'OR'
        };
        filter.activeFilterGroups.multi = true;
      }
    }

    // Handle direct key string (new direct filter support)
    if (filtersObj.key && typeof filtersObj.key === 'string' && allowedFilterTypes.includes('keys')) {
      filter.subFilters.filters.push({
        key: 'key',
        path: 'keys',
        value: {
          label: filtersObj.key,
          value: filtersObj.key
        }
      });
      filter.subFilters.active = true;
      filter.activeFilterGroups.single = true;
    }

    // Handle direct agency string (new direct filter support)
    if (filtersObj.agency && typeof filtersObj.agency === 'string' && allowedFilterTypes.includes('agencies')) {
      filter.subFilters.filters.push({
        key: 'agency',
        path: 'agency',
        value: {
          label: filtersObj.agency,
          value: filtersObj.agency
        }
      });
      filter.subFilters.active = true;
      filter.activeFilterGroups.single = true;
    }

    // Handle direct client string (new direct filter support)
    if (filtersObj.client && typeof filtersObj.client === 'string' && allowedFilterTypes.includes('clients')) {
      filter.subFilters.filters.push({
        key: 'client',
        path: 'client',
        value: {
          label: filtersObj.client,
          value: filtersObj.client
        }
      });
      filter.subFilters.active = true;
      filter.activeFilterGroups.single = true;
    }

    // Handle direct producer string (new direct filter support)
    if (filtersObj.producer && typeof filtersObj.producer === 'string' && allowedFilterTypes.includes('producers')) {
      filter.subFilters.filters.push({
        key: 'producer',
        path: 'producer',
        value: {
          label: filtersObj.producer,
          value: filtersObj.producer
        }
      });
      filter.subFilters.active = true;
      filter.activeFilterGroups.single = true;
    }

    // Handle direct studio string (new direct filter support)
    if (filtersObj.studio && typeof filtersObj.studio === 'string' && allowedFilterTypes.includes('studios')) {
      filter.subFilters.filters.push({
        key: 'studio',
        path: 'studio',
        value: {
          label: filtersObj.studio,
          value: filtersObj.studio
        }
      });
      filter.subFilters.active = true;
      filter.activeFilterGroups.single = true;
    }

    // Handle direct composers array (new direct filter support)
    if (Array.isArray(filtersObj.composers) && filtersObj.composers.length > 0 && allowedFilterTypes.includes('composers')) {
      filtersObj.composers.forEach((composer: string) => {
        filter.subFilters.filters.push({
          key: 'composer',
          path: 'composers',
          value: {
            label: composer,
            value: composer
          }
        });
      });
      filter.subFilters.active = true;
      filter.activeFilterGroups.single = true;
    }

    // Handle direct creativedirectors array (new direct filter support)
    if (Array.isArray(filtersObj.creativedirectors) && filtersObj.creativedirectors.length > 0 && allowedFilterTypes.includes('creativeDirectors')) {
      filtersObj.creativedirectors.forEach((director: string) => {
        filter.subFilters.filters.push({
          key: 'creativedirector',
          path: 'creativedirector',
          value: {
            label: director,
            value: director
          }
        });
      });
      filter.subFilters.active = true;
      filter.activeFilterGroups.single = true;
    }

    // Handle direct sessionartists array (new direct filter support)
    if (Array.isArray(filtersObj.sessionartists) && filtersObj.sessionartists.length > 0 && allowedFilterTypes.includes('sessionArtists')) {
      filtersObj.sessionartists.forEach((artist: string) => {
        filter.subFilters.filters.push({
          key: 'sessionartist',
          path: 'sessionartists',
          value: {
            label: artist,
            value: artist
          }
        });
      });
      filter.subFilters.active = true;
      filter.activeFilterGroups.single = true;
    }

    // Handle BPM filter
    if (filtersObj.bpm && allowedFilterTypes.includes('bpm')) {
      const bpmValue = filtersObj.bpm;
      if (bpmValue.includes('-')) {
        // Handle BPM range (including partial ranges like "140-" or "-80")
        const [minStr, maxStr] = bpmValue.split('-');
        const min = minStr && minStr.trim() !== '' ? Number(minStr) : null;
        const max = maxStr && maxStr.trim() !== '' ? Number(maxStr) : null;
        
        // Handle complete range (min-max)
        if (min !== null && max !== null && !isNaN(min) && !isNaN(max)) {
          filter.subFilters.filters.push({
            key: 'bpm',
            path: 'bpm',
            value: {
              label: `${min}-${max}`,
              value: `${min}-${max}`
            }
          });
          filter.subFilters.active = true;
          filter.activeFilterGroups.single = true;
        }
        // Handle minimum-only range (min-)
        else if (min !== null && !isNaN(min) && (maxStr === '' || maxStr === undefined)) {
          filter.subFilters.filters.push({
            key: 'bpm',
            path: 'bpm',
            value: {
              label: `${min}+`,
              value: `${min}-`
            }
          });
          filter.subFilters.active = true;
          filter.activeFilterGroups.single = true;
        }
        // Handle maximum-only range (-max)
        else if (max !== null && !isNaN(max) && (minStr === '' || minStr === undefined)) {
          filter.subFilters.filters.push({
            key: 'bpm',
            path: 'bpm',
            value: {
              label: `<${max}`,
              value: `-${max}`
            }
          });
          filter.subFilters.active = true;
          filter.activeFilterGroups.single = true;
        }
      } else {
        // Handle single BPM
        const bpm = Number(bpmValue);
        if (!isNaN(bpm)) {
          filter.subFilters.filters.push({
            key: 'bpm',
            path: 'bpm',
            value: {
              label: bpm.toString(),
              value: bpm.toString()
            }
          });
          filter.subFilters.active = true;
          filter.activeFilterGroups.single = true;
        }
      }
    }

    // Handle date keywords
    if (filtersObj.date && Array.isArray(filtersObj.date) && allowedFilterTypes.includes('date')) {
      const dateKeywords = filtersObj.date.filter((dateStr: string) =>
        dateStr.match(/^[\d]{1,2}\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+[\d]{4}$/i) ||
        dateStr.match(/^(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+[\d]{1,2}(?:st|nd|rd|th)?,\s+[\d]{4}$/i)
      );

      if (dateKeywords.length >= 2) {
        // Sort dates to ensure start date is before end date
        const sortedDates = dateKeywords.sort((a: string, b: string) => {
          const dateA = new Date(a);
          const dateB = new Date(b);
          return dateA.getTime() - dateB.getTime();
        });

        const startDate = new Date(sortedDates[0]);
        const endDate = new Date(sortedDates[sortedDates.length - 1]);
        
        // Set end date to end of day
        endDate.setHours(23, 59, 59, 999);

        filter.date = {
          active: true,
          from: startDate.getTime(),
          to: endDate.getTime(),
          type: 'completed'
        };
        filter.activeFilterGroups.date = true;
      } else if (dateKeywords.length === 1) {
        // Single date - use as start date
        const date = new Date(dateKeywords[0]);
        filter.date = {
          active: true,
          from: date.getTime(),
          to: Date.now(),
          type: 'completed'
        };
        filter.activeFilterGroups.date = true;
      }
    }

    // Handle license filter
    if (filtersObj.license && allowedFilterTypes.includes('license')) {
      filter.license = {
        active: true,
        value: filtersObj.license === 'licensed'
      };
      filter.activeFilterGroups.license = true;
    }

    // Handle studio filter
    if (filtersObj.studio) {
      // Get the collection cache state
      const collectionCache = useCollectionCache.getState();
      
      // Check if cache is initialized and studio collection exists
      if (collectionCache.isInitialized && collectionCache.studio) {
        // Find all studio matches that include the keyword
        const studioMatches = collectionCache.studio.filter(item => {
          const label = item.displayName || item.label || item.name || '';
          return label.toLowerCase().includes(filtersObj.studio.toLowerCase());
        });
        
        // Add all matching studios to the filter
        studioMatches.forEach(studio => {
          filter.subFilters.filters.push({
            key: 'studio',
            path: 'studio',
            value: {
              label: studio.displayName || studio.label || studio.name,
              value: studio.displayName || studio.label || studio.name
            }
          });
        });
      } else {
        // Fallback to single studio match if cache not available
        filter.subFilters.filters.push({
          key: 'studio',
          path: 'studio',
          value: {
            label: filtersObj.studio,
            value: filtersObj.studio
          }
        });
      }
    }

    // --- Genre AND/OR logic ---
    let genreLogic: 'and' | 'or' = 'or';
    if (logic?.genre === 'and' || logic?.genre === 'or') {
      genreLogic = logic.genre;
    }
    // --- Tag AND/OR logic ---
    let tagLogic: 'and' | 'or' = 'or';
    if (logic?.tag === 'and' || logic?.tag === 'or') {
      tagLogic = logic.tag;
    }
    // --- Tag2 AND/OR logic ---
    let tag2Logic: 'and' | 'or' = 'or';
    if (logic?.tag2 === 'and' || logic?.tag2 === 'or') {
      tag2Logic = logic.tag2;
    }
    // After all keyword/tag/genre matching logic, apply the logic to the filter:
    // For tags (multi)
    filter.multi.searchOperator = tagLogic.toUpperCase() as 'AND' | 'OR';
    // Store tag2 logic
    if (filter.subFilters.filters.some(f => f.key === 'tag2') || filter.multi.value === 'tags2') {
      (filter as any).tag2Logic = tag2Logic;
    }
    // For genres (subFilters)
    // If there are genre subFilters, store the logic in a property for later use (e.g., in the UI or when building the filter state)
    if (filter.subFilters.filters.some(f => f.key === 'genre')) {
      // Attach a custom property to subFilters for genre logic (if needed by downstream code)
      (filter.subFilters as any).genreLogic = genreLogic;
    }

    // Handle client filter
    if (filtersObj.client) {
      // Convert to proper case (e.g., "Client Name" instead of "client name")
      const clientValue = filtersObj.client.split(' ').map((word: string) => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
      
      filter.subFilters.filters.push({
        key: 'client',
        path: 'client',
        value: {
          label: clientValue,
          value: clientValue
        }
      });
    }

    // Handle agency filter
    if (filtersObj.agency) {
      filter.subFilters.filters.push({
        key: 'agency',
        path: 'agency',
        value: {
          label: filtersObj.agency,
          value: filtersObj.agency
        }
      });
    }

    // Handle producer filter
    if (filtersObj.producer) {
      filter.subFilters.filters.push({
        key: 'producer',
        path: 'producer',
        value: {
          label: filtersObj.producer,
          value: filtersObj.producer
        }
      });
    }

    // Handle composer filter
    if (filtersObj.composer) {
      filter.subFilters.filters.push({
        key: 'composer',
        path: 'composers',
        value: {
          label: filtersObj.composer,
          value: filtersObj.composer
        }
      });
    }

    // Handle key filter with consistent case handling
    if (filtersObj.key) {
      // Convert to proper case (e.g., "A Minor" instead of "A minor")
      const keyValue = filtersObj.key.split(' ').map((word: string) => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
      
      filter.subFilters.filters.push({
        key: 'key',
        path: 'keys',
        value: {
          label: keyValue,
          value: keyValue
        }
      });
    }

    // Handle year filter
    if (filtersObj.year) {
      filter.subFilters.filters.push({
        key: 'year',
        path: 'year',
        value: {
          label: filtersObj.year.toString(),
          value: filtersObj.year.toString()
        }
      });
    }

    return filter;
  }

  private combineFilters(filters: SearchFilter[]): SearchFilter {
    // Create the base filter structure
    const combinedFilter: SearchFilter = {
      multi: { active: false, data: [], value: '', searchOperator: 'OR' },
      date: { active: false, type: 'completed', from: 0, to: 0 },
      subFilters: { active: false, filters: [] },
      text: { active: false, value: '', field: 'trackName' },
      activeFilterGroups: {
        multi: false,
        date: false,
        single: false,
        text: false,
        license: false
      }
    };

    // If we have filters to combine
    if (filters && filters.length > 0) {
      // Collect all sub-filters while avoiding duplicates
      const seenFilters = new Set<string>();
      const allSubFilters: SubFilter[] = [];
      const multiFiltersByType: { [key: string]: any[] } = {};
      let hasLicenseFilter = false;
      let licenseValue = false;
      let hasDateFilter = false;
      let dateFilter: any = null;

      filters.forEach((filter: SearchFilter) => {
        // Handle license filter - only if it's explicitly set to active
        if (filter.license?.active === true) {
          hasLicenseFilter = true;
          licenseValue = filter.license.value;
        }

        // Handle date filter
        if (filter.date?.active === true) {
          hasDateFilter = true;
          dateFilter = {
            active: true,
            from: filter.date.from,
            to: filter.date.to,
            type: filter.date.type
          };
        }

        // Handle sub-filters
        if (filter.subFilters?.filters) {
          filter.subFilters.filters.forEach((subFilter: SubFilter) => {
            if (subFilter?.path && subFilter?.value?.value) {
              const filterKey = `${subFilter.path}-${subFilter.value.value}`;
              if (!seenFilters.has(filterKey)) {
                seenFilters.add(filterKey);
                allSubFilters.push(subFilter);
              }
            }
          });
        }

        // Handle multi-filters
        if (filter.multi?.active && filter.multi?.data?.length > 0) {
          const multiType = filter.multi.value;
          if (!multiFiltersByType[multiType]) {
            multiFiltersByType[multiType] = [];
          }
          
          // Add all data from this multi-filter to the appropriate type
          multiFiltersByType[multiType] = Array.from(new Set([
            ...multiFiltersByType[multiType], 
            ...filter.multi.data
          ]));
        }
      });

      // Update the combined filter
      combinedFilter.subFilters.filters = allSubFilters;
      combinedFilter.subFilters.active = allSubFilters.length > 0;
      combinedFilter.activeFilterGroups.single = allSubFilters.length > 0;

      // Handle multi-filters - create a separate filter for each type
      const multiFilterTypes = Object.keys(multiFiltersByType);
      if (multiFilterTypes.length > 0) {
        // Use the first multi-filter type as the primary one
        const primaryMultiType = multiFilterTypes[0];
        combinedFilter.multi.value = primaryMultiType;
        combinedFilter.multi.data = multiFiltersByType[primaryMultiType];
        combinedFilter.multi.active = true;
        combinedFilter.activeFilterGroups.multi = true;
        
        // Add the other multi-filter types as sub-filters
        multiFilterTypes.slice(1).forEach(multiType => {
          // Get the data for this multi-filter type
          const multiData = multiFiltersByType[multiType];
          
          // Add each item in the multi-filter as a separate sub-filter
          multiData.forEach(item => {
            combinedFilter.subFilters.filters.push({
              key: multiType,
              path: multiType,
              value: {
                label: item.label,
                value: item.value
              }
            });
          });
        });
        
        // If we added any sub-filters, make sure the sub-filters are active
        if (multiFilterTypes.length > 1) {
          combinedFilter.subFilters.active = true;
          combinedFilter.activeFilterGroups.single = true;
        }
      }

      // Handle date filter
      if (hasDateFilter) {
        combinedFilter.date = dateFilter;
        combinedFilter.activeFilterGroups.date = true;
      }

      // Handle license filter
      if (hasLicenseFilter) {
        combinedFilter.license = {
          active: true,
          value: licenseValue
        };
      }
    }

    return combinedFilter;
  }

  private convertToMappedFilters(filter: SearchFilter): MappedFilter[] {
    const mappedFilters: MappedFilter[] = [];

    // Convert sub-filters
    if (filter.subFilters.active && filter.subFilters.filters.length > 0) {
      filter.subFilters.filters.forEach(subFilter => {
        mappedFilters.push({
          type: subFilter.key,
          value: subFilter.value.value
        });
      });
    }

    // Convert multi-filters (tags, creativedirector, sessionartists)
    if (filter.multi.active && filter.multi.data.length > 0) {
      mappedFilters.push({
        type: filter.multi.value,
        value: filter.multi.data.map(item => item.value)
      });
    }

    // Convert date filter
    if (filter.date.active) {
      mappedFilters.push({
        type: 'date',
        value: {
          filterType: filter.date.type,
          range: {
            from: filter.date.from,
            to: filter.date.to
          }
        }
      });
    }

    // Convert license filter - only if it's explicitly set
    if (filter.license?.active === true) {
      mappedFilters.push({
        type: 'license',
        value: filter.license.value ? 'licensed' : 'unlicensed'
      });
    }

    return mappedFilters;
  }

  private generateExplanation(directMatches: any[], validatedExtractedData: ExtractedKeywords): string {
    // If we have a natural language response from Gemini, use it
    if (validatedExtractedData.naturalLanguageResponse) {
      return validatedExtractedData.naturalLanguageResponse;
    }

    // Fallback to our own explanation if no natural language response is available
    const parts: string[] = [];

    // Add direct match explanations
    if (directMatches.length > 0) {
      parts.push('Found direct matches for: ' + 
        directMatches.map(m => m.filter.subFilters.filters[0]?.value.label).join(', '));
    }

    // Add filters explanations
    const filtersParts = [];
    for (const [key, value] of Object.entries(validatedExtractedData.filters)) {
      if (value) {
        filtersParts.push(`${key}: ${value}`);
      }
    }
    if (filtersParts.length > 0) {
      parts.push('Filters information: ' + filtersParts.join(', '));
    }

    // Add confidence information
    if (validatedExtractedData.confidence) {
      const highConfidence = Object.entries(validatedExtractedData.confidence)
        .filter(([_, score]) => score >= 0.8)
        .map(([term]) => term);
      if (highConfidence.length > 0) {
        parts.push('High confidence terms: ' + highConfidence.join(', '));
      }
    }

    return parts.join('. ');
  }

  private async mapFiltersToSearchQuery(filters: SubFilter[], firebase: any): Promise<SubFilter[]> {
    const mappedFilters = await Promise.all(
      filters.map(async (subFilter: SubFilter) => {
        const mappedFilter = await filterMapper.findDirectMatches([subFilter.value.value], firebase);
        if (mappedFilter && mappedFilter.length > 0) {
          return mappedFilter[0].filter.subFilters.filters[0];
        }
        return subFilter;
      })
    );
    return mappedFilters;
  }

  /**
   * Validates Gemini's filter response against collection data to filter out non-existent terms
   * @param extractedData - The raw response from Gemini
   * @param collectionCache - The cached collection data
   * @returns Validated extracted data with non-existent terms moved to keywords
   */
  private validateGeminiFilters(extractedData: ExtractedKeywords, collectionCache: any): ExtractedKeywords {
    // Create a copy to avoid mutating the original
    const validated = JSON.parse(JSON.stringify(extractedData));
    
    // If no filters exist, return as is
    if (!validated.filters) {
      return validated;
    }

    // Get available collection terms
    const availableTags = new Set(
      collectionCache.tags?.map((item: any) => item.displayName) || []
    );
    const availableGenres = new Set(
      collectionCache.genres?.map((item: any) => item.displayName) || []
    );

    // Initialize keywords array if it doesn't exist
    if (!validated.keywords) {
      validated.keywords = [];
    }

    // Validate tags array
    if (Array.isArray(validated.filters.tags)) {
      const validTags: string[] = [];
      validated.filters.tags.forEach((tag: string) => {
        if (availableTags.has(tag)) {
          validTags.push(tag);
        } else {
          // Add invalid tag to keywords array to preserve the intent
          if (!validated.keywords.includes(tag)) {
            validated.keywords.push(tag);
          }
        }
      });
      validated.filters.tags = validTags;
    }

    // Validate tags2 array
    if (Array.isArray(validated.filters.tags2)) {
      const validTags2: string[] = [];
      validated.filters.tags2.forEach((tag: string) => {
        if (availableTags.has(tag)) {
          validTags2.push(tag);
        } else {
          // Add invalid tag to keywords array to preserve the intent
          if (!validated.keywords.includes(tag)) {
            validated.keywords.push(tag);
          }
        }
      });
      validated.filters.tags2 = validTags2;
    }

    // Validate genres array
    if (Array.isArray(validated.filters.genres)) {
      const validGenres: string[] = [];
      validated.filters.genres.forEach((genre: string) => {
        if (availableGenres.has(genre)) {
          validGenres.push(genre);
        } else {
          // Add invalid genre to keywords array to preserve the intent
          if (!validated.keywords.includes(genre)) {
            validated.keywords.push(genre);
          }
        }
      });
      validated.filters.genres = validGenres;
    }


    return validated;
  }
}

export const filterService = new FilterService(); 